/**
 * 「查一台機器」— the global machine-lookup drawer (boards/Lookup.dc.html). One drawer for the
 * whole console, opened from the sidebar button, Ctrl/Cmd+K, a roster row, a license's bound SN
 * or a ticket's reporter SN. admin+ only (GET /api/machine-lookup is admin-gated); below that
 * open() is a no-op and the shortcut is not bound.
 *
 * `changeTick` bumps after any write made from the drawer so open pages can re-poll.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { roleAtLeast } from '@/lib/roles';
import { LookupDrawer } from '@/components/LookupDrawer';

interface LookupState {
  enabled: boolean;
  open: (query?: string) => void;
  close: () => void;
  changeTick: number;
  notifyChanged: () => void;
}

const Ctx = createContext<LookupState>({
  enabled: false,
  open: () => undefined,
  close: () => undefined,
  changeTick: 0,
  notifyChanged: () => undefined,
});

export function LookupProvider({ children }: { children: ReactNode }) {
  const { role } = useAuth();
  const enabled = roleAtLeast(role, 'admin');
  const [query, setQuery] = useState<string | null>(null);
  const [changeTick, setChangeTick] = useState(0);

  const open = useCallback((q?: string) => {
    if (enabled) setQuery(q ?? '');
  }, [enabled]);
  const close = useCallback(() => setQuery(null), []);
  const notifyChanged = useCallback(() => setChangeTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setQuery((cur) => (cur === null ? '' : cur));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);

  const value = useMemo(() => ({ enabled, open, close, changeTick, notifyChanged }), [enabled, open, close, changeTick, notifyChanged]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {enabled && query !== null && (
        <LookupDrawer key={query} initialQuery={query} onClose={close} onChanged={notifyChanged} />
      )}
    </Ctx.Provider>
  );
}

export function useLookup(): LookupState {
  return useContext(Ctx);
}
